import { useEffect, useMemo, useRef, useState } from "react";

const INTERVAL_MS = 4500;
const CAN_HOVER = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export const ServiceSlideshow = ({ images, fallback, alt, fit = "object-cover", testId, children }) => {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);
  const touchStartX = useRef(null);

  // Default image is always first; admin "I slideshow" images follow in "Ordning"
  const slides = useMemo(
    () => [{ id: "default", src: fallback, fit }, ...images.map((img) => ({ ...img, fit: "object-cover" }))],
    [images, fallback, fit]
  );
  const count = slides.length;
  const active = count > 1 && (CAN_HOVER ? hovered : visible);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || count < 2 || CAN_HOVER) return;
    const obs = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [count]);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    const t = setInterval(() => setIndex((i) => (i + 1) % count), INTERVAL_MS);
    return () => clearInterval(t);
  }, [active, count]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null || count < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    setIndex((i) => (dx < 0 ? (i + 1) % count : (i - 1 + count) % count));
  };

  return (
    <div
      ref={rootRef}
      data-testid={testId}
      data-slide-index={index}
      data-slide-count={count}
      data-slide-active={active}
      onMouseEnter={() => CAN_HOVER && setHovered(true)}
      onMouseLeave={() => CAN_HOVER && setHovered(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative w-full h-full"
    >
      {slides.map((img, i) => (
        <img
          key={img.id}
          src={img.src}
          alt={i === 0 ? alt : `${alt} ${i}`}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full transition-[opacity,transform] duration-1000 ease-in-out group-hover:scale-105 ${img.fit} ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      {children}
      {count > 1 && (
        <div
          data-testid={`${testId}-dots`}
          className="absolute bottom-3 left-0 right-0 z-[3] flex items-center justify-center gap-1.5"
        >
          {slides.map((img, i) => (
            <button
              key={img.id}
              type="button"
              aria-label={`Bild ${i + 1} av ${count}`}
              aria-current={i === index}
              data-testid={`${testId}-dot-${i}`}
              onClick={(e) => { e.stopPropagation(); setIndex(i); }}
              className={`h-1.5 transition-[width,background-color] duration-300 shadow-[0_0_0_1px_rgba(11,19,43,0.6)] ${
                i === index ? "w-6 bg-[#D92525]" : "w-1.5 bg-white/70 hover:bg-white"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
